import { __decorate } from "tslib";
import { html, LitElement } from 'lit';
import { property, state } from 'lit/decorators.js';
import { createRef, ref } from 'lit/directives/ref.js';
import { styleMap } from 'lit/directives/style-map.js';
import { ifNonEmpty } from '../../base/directives';
import { WithFocus } from '../../base/elements';
import { eventListener, vdsEvent } from '../../base/events';
import { ElementLogger } from '../../base/logger';
import { DEV_MODE } from '../../global/env';
import { clampNumber, getNumberOfDecimalPlaces, round } from '../../utils/number';
import { rafThrottle } from '../../utils/timing';
import { sliderElementStyles } from './styles';
export const SLIDER_ELEMENT_TAG_NAME = 'vds-slider';
/**
 * The direction to move the thumb, associated with key symbols.
 */
export var SliderKeyDirection;
(function (SliderKeyDirection) {
    SliderKeyDirection[SliderKeyDirection["Left"] = -1] = "Left";
    SliderKeyDirection[SliderKeyDirection["ArrowLeft"] = -1] = "ArrowLeft";
    SliderKeyDirection[SliderKeyDirection["Up"] = -1] = "Up";
    SliderKeyDirection[SliderKeyDirection["ArrowUp"] = -1] = "ArrowUp";
    SliderKeyDirection[SliderKeyDirection["Right"] = 1] = "Right";
    SliderKeyDirection[SliderKeyDirection["ArrowRight"] = 1] = "ArrowRight";
    SliderKeyDirection[SliderKeyDirection["Down"] = 1] = "Down";
    SliderKeyDirection[SliderKeyDirection["ArrowDown"] = 1] = "ArrowDown";
})(SliderKeyDirection || (SliderKeyDirection = {}));
/**
 * A custom built `input[type="range"]` that is cross-browser friendly, ARIA friendly, mouse/touch
 * friendly and easily styleable. This component allows users to input numeric values between a
 * minimum and maxmimum value. Generally used in the player for volume or scrubber controls.
 *
 * @see https://github.com/carbon-design-system/carbon-web-components
 * @tagname vds-slider
 * @slot Used to pass in additional content inside the slider.
 * @slot thumb-container - Used to pass content into the thumb container.
 * @slot thumb - Used to pass content inside the thumb.
 * @slot track - Used to pass content inside the track.
 * @slot track-fill - Used to pass content inside the track fill.
 * @csspart root - The component's root element, in this case the slider container (`<div>`).
 * @csspart thumb-container - The container for the slider's handle.
 * @csspart thumb - The slider's handle the user drags left/right (`<div>`).
 * @csspart track - The path in which the thumb slides along (`<div>`).
 * @csspart track-fill - The part of the track that is currently filled which fills left-to-right (`<div>`).
 * @cssprop --vds-slider-fill-rate - The ratio of the slider that is filled such as `0.3`.
 * @cssprop --vds-slider-fill-value - The current amount the slider is filled such as `30`.
 * @cssprop --vds-slider-fill-percentage - The fill rate expressed as a precetange such as `30%`.
 * @cssprop --vds-slider-thumb-width - The slider handle width.
 * @cssprop --vds-slider-thumb-height - The slider handle height.
 * @cssprop --vds-slider-thumb-bg - The background color of the slider handle.
 * @cssprop --vds-slider-thumb-border-radius - The border radius of the slider handle.
 * @cssprop --vds-slider-thumb-scale - The base scale of thumb when it is inactive, it'll scale to 1 when active.
 * @cssprop --vds-slider-thumb-transition - The CSS transitions to use for the thumb, defaults to `transform 100ms ease-out 0s`.
 * @cssprop --vds-slider-track-height - The height of the slider track.
 * @cssprop --vds-slider-track-bg - The background color of the slider track.
 * @cssprop --vds-slider-track-fill-bg - The background color of the slider track fill.
 * @cssprop --vds-slider-active-color - The slider thumb and track fill background color when focused, active or being dragged.
 * @cssprop --vds-slider-disabled-color - The slider thumb, track, and track fill background color when disabled.
 * @example
 * ```html
 * <vds-slider
 *   min="0"
 *   max="100"
 *   value="50"
 * ></vds-slider>
 * ```
 * @example
 * ```css
 * vds-slider {
 *   --vds-slider-active-color: #ff2a5d;
 * }
 *
 * vds-slider::part(thumb) {
 *   box-shadow: transparent 0px 0px 0px 1px inset;
 * }
 *
 * vds-slider::part(track),
 * vds-slider::part(track-fill) {
 *   border-radius: 3px;
 * }
 * ```
 */
export class SliderElement extends WithFocus(LitElement) {
    constructor() {
        super(...arguments);
        // -------------------------------------------------------------------------------------------
        // Properties
        // -------------------------------------------------------------------------------------------
        /* c8 ignore next */
        this._logger = DEV_MODE && new ElementLogger(this);
        /**
         * The lowest slider value in the range of permitted values.
         */
        this.min = 0;
        /**
         * The greatest slider value in the range of permitted values.
         */
        this.max = 100;
        /**
         * Whether the slider should be hidden.
         */
        this.hidden = false;
        /**
         * Whether the slider should be disabled (not-interactable).
         */
        this.disabled = false;
        /**
         * The current slider value.
         */
        this.value = 50;
        /**
         * ♿ **ARIA:** Indicates the orientation of the slider.
         */
        this.orientation = 'horizontal';
        /**
         * A number that specifies the granularity that the slider value must adhere to.
         */
        this.step = 1;
        /**
         * ♿ **ARIA:** A number that specifies the number of steps taken when interacting with
         * the slider via keyboard.
         */
        this.keyboardStep = 1;
        /**
         * ♿ **ARIA:** A number that will be used to multiply the `keyboardStep` when the `Shift` key
         * is held down and the slider value is changed by pressing `LeftArrow` or `RightArrow`. Think
         * of it as `keyboardStep * shiftKeyMultiplier`.
         */
        this.shiftKeyMultiplier = 5;
        this._isDragging = false;
        // -------------------------------------------------------------------------------------------
        // Render (Root/Slider)
        // -------------------------------------------------------------------------------------------
        this._rootRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Render (Thumb Container)
        // -------------------------------------------------------------------------------------------
        this._thumbContainerRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Render (Thumb)
        // -------------------------------------------------------------------------------------------
        this._thumbRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Render (Track)
        // -------------------------------------------------------------------------------------------
        this._trackRef = createRef();
        // -------------------------------------------------------------------------------------------
        // Render (Track Fill)
        // -------------------------------------------------------------------------------------------
        this._trackFillRef = createRef();
        this._handlePointerMove = rafThrottle((event) => {
            if (this.disabled || !this._isDragging)
                return;
            this._updateValueBasedOnThumbPosition(event);
            this._dispatchValueChange(event);
        });
        this._lastDispatchedValue = this.value;
        this._dispatchValueChange = rafThrottle((event) => {
            if (this.value === this._lastDispatchedValue)
                return;
            this.dispatchEvent(vdsEvent('vds-slider-value-change', {
                detail: this.value,
                originalEvent: event
            }));
            this._lastDispatchedValue = this.value;
        });
    }
    static get styles() {
        return [sliderElementStyles];
    }
    static get parts() {
        return ['root', 'thumb', 'track', 'track-fill'];
    }
    /**
     * Whether the slider thumb is currently being dragged.
     *
     * @default false
     */
    get isDragging() {
        return this._isDragging;
    }
    /**
     * The current value to range ratio.
     *
     * @default 0.5
     * @example
     * `min` = 0
     * `max` = 10
     * `value` = 5
     * `range` = 10 (max - min)
     * `fillRate` = 0.5 (result)
     */
    get fillRate() {
        const range = this.max - this.min;
        return this.value / range;
    }
    /**
     * The fill rate expressed as a percentage (`fillRate * 100`).
     *
     * @default 50
     */
    get fillPercent() {
        return this.fillRate * 100;
    }
    // -------------------------------------------------------------------------------------------
    // Lifecycle
    // -------------------------------------------------------------------------------------------
    update(changedProperties) {
        if (changedProperties.has('value')) {
            this._updateValue(this.value);
        }
        if (changedProperties.has('disabled') && this.disabled) {
            this._isDragging = false;
            this.removeAttribute('dragging');
        }
        super.update(changedProperties);
    }
    disconnectedCallback() {
        this._handlePointerMove.cancel();
        super.disconnectedCallback();
    }
    /**
     * The component's root element.
     */
    get rootElement() {
        return this._rootRef.value;
    }
    render() {
        return this._renderSlider();
    }
    _renderSlider() {
        return html `
      <div
        id="root"
        role="presentation"
        part="root"
        style=${styleMap({
            '--vds-slider-fill-value': String(this.value),
            '--vds-slider-fill-rate': String(this.fillRate),
            '--vds-slider-fill-percent': `${this.fillPercent}%`
        })}
        @pointerdown=${this._handleSliderPointerMove}
        ${ref(this._rootRef)}
      >
        ${this._renderSliderChildren()}
      </div>
    `;
    }
    _renderSliderChildren() {
        return html `${this._renderThumbContainer()}${this._renderTrack()}${this._renderTrackFill()}${this._renderInput()}${this._renderDefaultSlot()}`;
    }
    _renderDefaultSlot() {
        return html `<slot></slot>`;
    }
    _handleSliderPointerMove(event) {
        if (this.disabled)
            return;
        this._startDragging(event);
        this._handlePointerMove(event);
    }
    /**
     * The thumb container element.
     */
    get thumbContainerElement() {
        return this._thumbContainerRef.value;
    }
    _renderThumbContainer() {
        return html `
      <div
        id="thumb-container"
        role="slider"
        tabindex="0"
        aria-label=${ifNonEmpty(this.label)}
        aria-valuemin=${this._getValueMin()}
        aria-valuenow=${this._getValueNow()}
        aria-valuemax=${this._getValueMax()}
        aria-valuetext=${this._getValueText()}
        aria-orientation=${this.orientation}
        aria-disabled=${this.disabled}
        aria-hidden=${this.hidden}
        autocomplete="off"
        part="thumb-container"
        @keydown=${this._handleThumbContainerKeydown}
        @pointerdown=${this._handleThumbContainerPointerDown}
        ${ref(this._thumbContainerRef)}
      >
        ${this._renderThumb()} ${this._renderThumbContainerSlot()}
      </div>
    `;
    }
    _getValueMin() {
        var _a;
        return (_a = this.valueMin) !== null && _a !== void 0 ? _a : String(this.min);
    }
    _getValueNow() {
        var _a;
        return (_a = this.valueNow) !== null && _a !== void 0 ? _a : String(this.value);
    }
    _getValueMax() {
        var _a;
        return (_a = this.valueMax) !== null && _a !== void 0 ? _a : String(this.max);
    }
    _getValueText() {
        var _a;
        return (_a = this.valueText) !== null && _a !== void 0 ? _a : this._getValueTextFallback();
    }
    _getValueTextFallback() {
        return `${round((this.value / this.max) * 100, 2)}%`;
    }
    _renderThumbContainerSlot() {
        return html `<slot name="thumb-container"></slot> `;
    }
    _handleThumbContainerKeydown(event) {
        if (this.disabled)
            return;
        const { key, shiftKey } = event;
        const isValidKey = Object.keys(SliderKeyDirection).includes(key);
        if (!isValidKey)
            return;
        const modifiedStep = !shiftKey
            ? this.keyboardStep
            : this.keyboardStep * this.shiftKeyMultiplier;
        const direction = Number(SliderKeyDirection[key]);
        const diff = modifiedStep * direction;
        const steps = (this.value + diff) / this.step;
        const value = this.step * steps;
        this._updateValue(value);
        this._dispatchValueChange(event);
    }
    _handleThumbContainerPointerDown(event) {
        if (this.disabled)
            return;
        this._startDragging(event);
    }
    /**
     * The thumb element.
     */
    get thumbElement() {
        return this._thumbRef.value;
    }
    _renderThumb() {
        return html `
      <div id="thumb" part="thumb" ${ref(this._thumbRef)}>
        ${this._renderThumbSlot()}
      </div>
    `;
    }
    _renderThumbSlot() {
        return html `<slot name="thumb"></slot>`;
    }
    /**
     * The track element.
     */
    get trackElement() {
        return this._trackRef.value;
    }
    _renderTrack() {
        return html `
      <div id="track" part="track" ${ref(this._trackRef)}>
        ${this._renderTrackSlot()}
      </div>
    `;
    }
    _renderTrackSlot() {
        return html `<slot name="track"></slot>`;
    }
    /**
     * The track fill element.
     */
    get trackFillElement() {
        return this._trackFillRef.value;
    }
    _renderTrackFill() {
        return html `
      <div id="track-fill" part="track-fill" ${ref(this._trackFillRef)}>
        ${this._renderTrackFillSlot()}
      </div>
    `;
    }
    _renderTrackFillSlot() {
        return html `<slot name="track-fill"></slot>`;
    }
    // -------------------------------------------------------------------------------------------
    // Render (Input)
    // -------------------------------------------------------------------------------------------
    /**
     * Why? Used to emit native `input` events.
     */
    _renderInput() {
        return html `
      <input
        type="hidden"
        min="${this.min}"
        max="${this.max}"
        value="${this.value}"
      />
    `;
    }
    // -------------------------------------------------------------------------------------------
    // Drag
    // -------------------------------------------------------------------------------------------
    _startDragging(event) {
        if (this._isDragging)
            return;
        this._isDragging = true;
        this.setAttribute('dragging', '');
        this._updateValueBasedOnThumbPosition(event);
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup('started dragging')
                .appendWithLabel('Event', event)
                .end();
        }
        /* c8 ignore stop */
        this.dispatchEvent(vdsEvent('vds-slider-drag-start', {
            originalEvent: event,
            detail: this.value
        }));
    }
    _stopDragging(event) {
        if (!this._isDragging)
            return;
        this._isDragging = false;
        this._dispatchValueChange.cancel();
        this.removeAttribute('dragging');
        this._updateValueBasedOnThumbPosition(event);
        /* c8 ignore start */
        if (DEV_MODE) {
            this._logger
                .debugGroup('stopped dragging')
                .appendWithLabel('Event', event)
                .end();
        }
        /* c8 ignore stop */
        this.dispatchEvent(vdsEvent('vds-slider-drag-end', {
            originalEvent: event,
            detail: this.value
        }));
    }
    // -------------------------------------------------------------------------------------------
    // Document (Pointer Events)
    // -------------------------------------------------------------------------------------------
    _handleDocumentPointerUp(event) {
        if (this.disabled || !this._isDragging)
            return;
        this._stopDragging(event);
    }
    _handleDocumentPointerMove(event) {
        if (this.disabled || !this._isDragging) {
            this._handlePointerMove.cancel();
            return;
        }
        this._handlePointerMove(event);
    }
    _updateValue(value) {
        this.value = clampNumber(this.min, round(value, getNumberOfDecimalPlaces(this.step)), this.max);
    }
    _updateValueByRate(rate) {
        const boundRate = clampNumber(0, rate, 1);
        const range = this.max - this.min;
        const fill = range * boundRate;
        const stepRatio = Math.round(fill / this.step);
        const steps = this.step * stepRatio;
        const value = this.min + steps;
        this._updateValue(value);
    }
    _updateValueBasedOnThumbPosition(event) {
        const thumbClientX = event.clientX;
        const { left: trackLeft, width: trackWidth } = this.trackElement.getBoundingClientRect();
        const thumbPositionRate = (thumbClientX - trackLeft) / trackWidth;
        // Calling this will update `this.value`.
        this._updateValueByRate(thumbPositionRate);
    }
}
__decorate([
    property({ reflect: true })
], SliderElement.prototype, "label", void 0);
__decorate([
    property({ reflect: true, type: Number })
], SliderElement.prototype, "min", void 0);
__decorate([
    property({ reflect: true, type: Number })
], SliderElement.prototype, "max", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], SliderElement.prototype, "hidden", void 0);
__decorate([
    property({ reflect: true, type: Boolean })
], SliderElement.prototype, "disabled", void 0);
__decorate([
    property({ reflect: true, type: Number })
], SliderElement.prototype, "value", void 0);
__decorate([
    property({ attribute: 'value-now' })
], SliderElement.prototype, "valueNow", void 0);
__decorate([
    property({ attribute: 'value-max' })
], SliderElement.prototype, "valueMax", void 0);
__decorate([
    property({ attribute: 'value-text' })
], SliderElement.prototype, "valueText", void 0);
__decorate([
    property({ reflect: true })
], SliderElement.prototype, "orientation", void 0);
__decorate([
    property({ type: Number, reflect: true })
], SliderElement.prototype, "step", void 0);
__decorate([
    property({ attribute: 'keyboard-step', type: Number })
], SliderElement.prototype, "keyboardStep", void 0);
__decorate([
    property({ attribute: 'shift-key-multiplier', type: Number })
], SliderElement.prototype, "shiftKeyMultiplier", void 0);
__decorate([
    state()
], SliderElement.prototype, "_isDragging", void 0);
__decorate([
    eventListener('pointerup', { target: document })
], SliderElement.prototype, "_handleDocumentPointerUp", null);
__decorate([
    eventListener('pointermove', { target: document })
], SliderElement.prototype, "_handleDocumentPointerMove", null);
